import { ChevronLeft } from "lucide-react-native";
import { FC, useMemo, useState } from "react";

import { Box, Card, HStack, Pressable, Text, VStack } from "@/components/ui";

import { Segmented } from "./Controls";
import CountyMap from "./CountyMap";
import DistrictMap from "./DistrictMap";
import { STATES } from "./geo";
import { CountyLegend, NationalLegend } from "./Legend";
import type { County2024 } from "./model";
import NationalMap from "./NationalMap";
import type { Layer, ModelSettings, Office, Paint } from "./types";
import {
  type CountyLayer,
  countyPaints,
  OFFICE_LABEL,
  type RaceRow,
} from "./view";

/* ── Types ─────────────────────────────────────── */

interface TrackerMapProps {
  county2024: County2024 | null;
  /** House district fills, by district id. */
  districtPaints: Record<string, Paint>;
  /** The `turnoutEdge` the row was built with. */
  edge: number;
  /** The `nationalEnvironment` the row was built with. */
  environment: number | null;
  layer: Layer;
  office: Office;
  /** Back to the national map. */
  onBack: () => void;
  /** A state shape was clicked, by FIPS. */
  onSelect: (fips: string) => void;
  /** National map fills, by state FIPS. */
  paints: Record<string, Paint>;
  /** The compared source is the count. */
  results: boolean;
  /** The open state's race; null on the national map. */
  row: RaceRow | null;
  settings: ModelSettings;
  showIndependent: boolean;
  sourceLabel: string | null;
}

/* ── Constants ─────────────────────────────────── */

const COUNTY_LAYERS: { label: string; value: CountyLayer }[] = [
  { label: "Foresight", value: "foresight" },
  { label: "2024 result", value: "2024" },
  { label: "Shift vs 2024", value: "shift" },
];

/* ── Component ─────────────────────────────────── */

/** The national map, or the open state's county map (district map for the
    House), with its legend. */
const TrackerMap: FC<TrackerMapProps> = ({
  county2024,
  districtPaints,
  edge,
  environment,
  layer,
  office,
  onBack,
  onSelect,
  paints,
  results,
  row,
  settings,
  showIndependent,
  sourceLabel,
}) => {
  const [countyLayer, setCountyLayer] = useState<CountyLayer>("foresight");
  const [features, setFeatures] = useState<Record<string, GeoJSON.Feature>>({});
  const house = office === "house";

  const countyMapPaints = useMemo(
    () =>
      row && !house
        ? countyPaints({
            county2024,
            edge,
            environment,
            layer: countyLayer,
            office,
            row,
            settings,
          })
        : {},
    [county2024, countyLayer, edge, environment, house, office, row, settings]
  );

  const selectDistrictState = (abbr: string) => {
    const state = STATES.find((s) => s.abbr === abbr);
    if (state) onSelect(state.fips);
  };

  return (
    <Card className="flex-1 w-full min-w-0 p-5 rounded-2xl border border-outline-200">
      <VStack className="gap-4">
        {row ? (
          <HStack className="flex-wrap items-center justify-between gap-3">
            <Pressable className="flex-row items-center gap-1" onPress={onBack}>
              <ChevronLeft color="rgb(var(--color-typography-600))" size={16} />
              <Text className="text-sm font-medium text-typography-700">
                United States
              </Text>
              <Text className="text-sm text-typography-400">
                {" "}
                / {row.state.name} {house ? "districts" : "counties"}
              </Text>
            </Pressable>
            {!house && (
              <Segmented
                onChange={setCountyLayer}
                options={COUNTY_LAYERS}
                value={countyLayer}
              />
            )}
          </HStack>
        ) : (
          <Text className="text-sm text-typography-500">
            {house
              ? "All 435 districts as drawn for 2026. Click one for its state's districts."
              : `Only states with a ${OFFICE_LABEL[office]} race on the 2026 ballot are colored. Click one for its counties.`}
          </Text>
        )}

        <Box className="w-full max-w-[860px] self-center">
          {row && !house && (
            <CountyMap
              key={row.state.fips}
              paints={countyMapPaints}
              stateFeature={features[row.state.fips] ?? null}
              stateFips={row.state.fips}
            />
          )}
          {house && (
            <DistrictMap
              key={row?.state.fips ?? "US"}
              onSelect={row ? null : selectDistrictState}
              paints={districtPaints}
              state={
                row
                  ? {
                      abbr: row.state.abbr,
                      feature: features[row.state.fips] ?? null,
                      fips: row.state.fips,
                    }
                  : null
              }
            />
          )}
          {/* Stays mounted under the other maps: it loads the state shapes
              the county and district maps zoom on, even when a state is
              picked from the list before the map has drawn. */}
          <Box className={row || house ? "hidden" : "w-full"}>
            <NationalMap
              onFeatures={setFeatures}
              onSelect={onSelect}
              paints={paints}
              selected={row?.state.fips ?? null}
            />
          </Box>
        </Box>

        {row && !house ? (
          <CountyLegend layer={countyLayer} />
        ) : (
          <NationalLegend
            layer={layer}
            results={results}
            showIndependent={showIndependent}
            sourceLabel={sourceLabel}
          />
        )}
      </VStack>
    </Card>
  );
};

export default TrackerMap;
